import React, { useCallback, useContext, useState } from "react"
import I18n from "i18n-js"

import { openLogin, openRegistration } from "../gigya"
import { postForm } from "../utils"
import Button from "@root/components/Button"
import SectionTitle from "@root/components/SectionTitle"
import Carousel from "@root/components/Carousel"
import { useIsMobile } from "@root/misc/isMobile"

import { AppLinks } from "@root/types"
import iosStoreImg from "@images/download__ios.png"
import androidStoreImg from "@images/download__android.png"
import EnterCodesForIOSButton from "@root/components/EnterCodesForIOSButton"
import appLogoSrc from "@images/appLogo.png"

export interface CompInit {
  applinks: AppLinks
}

const TEASER_IMAGE_PROJECTS = [
  "laliga2026-27",
  // "fifae2025",
  // "brasileirao2025",
  // "laliga2025-26",
  // "fcbq24-25",
  // "foot2024",
  // "fifa365_2025",
  // "brasileirao2024",
  // "laliga2024-25",
  // "voleibol2024",
  // "asobal23-24",
  // "fcbq23-24",
  // "calciatori-femminile23-24",
  // "calciatori23-24",
  // "foot2023",
  // "ssl2023",
  // "teamchile2023",
  // "fifa365",
  // "laliga",
] as const

const __teaserImgContext = require.context("@images", true, /teaser--.*(jpg)$/)
const teaserImgSrc = (project: typeof TEASER_IMAGE_PROJECTS[number], locale: string) => {
  return __teaserImgContext(`./teaser--${project}--${locale.toLocaleLowerCase()}.jpg`)
}

export const LandingContext = React.createContext<{ comingSoon?: boolean, mode: "desktop" | "ios" | "android" }>({ mode: "desktop" })

function loginAsGuest() {
  postForm("/session/guest")
}

const b = (key: string) => `web.pages.landing.${key}`

const LinkToAllyInfo: React.FC<{className?: string}> = ({ className }) => <a href={I18n.t("url.a11y")} target="_blank" className={className}>{I18n.t("web.footer.app_a11y_statement")}</a>

const A11yLinks: React.FC = () => {
  return (
    <div className="neo__landing__a11ylinks">
      <LinkToAllyInfo className="neo__landing__a11ylinks__link"/>
    </div>
  )
}

export const AppLinksActionArea: React.FC<{appLinks: AppLinks}> = ({ appLinks }) => {
  return <div className="landing__appLinksActionArea">
    <div className="landing__appLinksActionArea__appLinks">
      <a className="landing__appLinksActionArea__appLinks__link" href={appLinks.ios!}><img src={iosStoreImg} /></a>
      <a className="landing__appLinksActionArea__appLinks__link" href={appLinks.android!}><img src={androidStoreImg} /></a>
    </div>
    <div className="landing__appLinksActionArea__allyInfo">
      <LinkToAllyInfo />
    </div>
  </div>
}

export const LandingActionArea: React.FC<{ appLinks: AppLinks }> = ({ appLinks }) => {
  const { mode, comingSoon } = useContext(LandingContext)

  return <>
    {mode === "ios" &&
      <div className="neo__landing__infoForMobileUsers">
        {comingSoon
          ? <div className="neo__landing__comingSoon__holder"><div className="neo__landing__comingSoon"><span>{I18n.t("coming_soon.title")}</span></div></div>
          : <>
              <div className="neo__landing__iosActionsHolder">
                <EnterCodesForIOSButton>{I18n.t("web.pages.landing.ios_action_enter_codes")}</EnterCodesForIOSButton>
                <Button href={appLinks.ios} target="_blank" className="neo__landing__buttonIOSGetGame">
                  <span className="neo__landing__buttonIOSGetGame__content">
                    <span>{I18n.t("web.pages.landing.ios_action_get_game")}</span>
                    <img src={iosStoreImg} />
                  </span>
                </Button>
              </div>
              <A11yLinks />
            </>
        }
      </div>}
    {mode === "android" &&
      <div className="neo__landing__infoForMobileUsers">
        {comingSoon
          ? <div className="neo__landing__comingSoon__holder"><div className="neo__landing__comingSoon"><span>{I18n.t("coming_soon.title")}</span></div></div>
          : <>
              <div className="neo__landing__androidActionsHolder">
                <a href={appLinks.ios}><img src={iosStoreImg} /></a>
                <a href={appLinks.android}><img src={androidStoreImg} /></a>
              </div>
              <A11yLinks />
            </>
        }
      </div>}
    {mode === "desktop" &&
      <>
        <div className="neo__landing__actions">
          {comingSoon && <div className="neo__landing__comingSoon"><span>{I18n.t("coming_soon.title")}</span></div>}
          <Button disabled={comingSoon} onClick={() => openLogin()}>{I18n.t("web.pages.landing.action_login")}</Button>
          <Button disabled={comingSoon} onClick={() => openRegistration()}>{I18n.t("web.pages.landing.action_register")}</Button>
          <Button disabled={comingSoon} onClick={() => loginAsGuest()}>{I18n.t("web.pages.landing.login_as_guest")}</Button>
        </div>
        <AppLinksActionArea appLinks={appLinks} />
        {!comingSoon && <div className="neo__landing__desktopEnterCodesButtonHolder">
          <EnterCodesForIOSButton>{I18n.t("web.pages.landing.desktop_action_enter_codes")}</EnterCodesForIOSButton>
        </div>}
      </>}
  </>
}

export const LandingFeatureDescription: React.FC = () => {
  return <div className="neo__landing__features">
    <div className="neo__landing__features__title">{I18n.t("web.pages.landing.features.title")}</div>
    <div className="neo__landing__features__list">
      {[1, 2, 3, 4].map((featureNum) => {
        return <div key={featureNum}>
          <div>{I18n.t(`web.pages.landing.features.feature${featureNum}title`)}</div>
          <div>{I18n.t(`web.pages.landing.features.feature${featureNum}info`)}</div>
        </div>
      })}
    </div>
  </div>
}

const TeaserSlide: React.FC<{ project: typeof TEASER_IMAGE_PROJECTS[number], comingSoon: boolean }> = ({ project, comingSoon }) => {
  const asteriskExplanation = I18n.t(`web.pages.landing.${project}.teaser_asterisk_explanation`, { defaultValue: "" })
  return (
    <div className="neo__landing__teaser" key={project}>
      <div className="neo__landing__teaser__holder">
        <img className="neo__landing__teaser" src={teaserImgSrc(project, I18n.locale)} />
        {comingSoon && <div className="neo__landing__hiddenStuff" onClick={() => location.href = "/curtain/raise"} />}
      </div>
      <div className="neo__landing__teaserInfos">
        <div className="neo__landing__infoBelowTeaser">{I18n.t(`web.pages.landing.${project}.info_below_teaser`)}</div>
        {(asteriskExplanation && asteriskExplanation.length > 0) &&
          <div className="neo__landing__teaserAsteriskExplanation">{"* "}{asteriskExplanation}</div>
        }
      </div>
    </div>
  )
}

const PrivacyInfo: React.FC = () => {
  return <div className="neo__landing__privacyInfo">
    <div dangerouslySetInnerHTML={{__html: I18n.t("web.pages.landing.privacy_info--html")}} />
  </div>
}

const Landing: React.FC<{ comingSoon?: boolean, init: CompInit }> = ({ comingSoon, init }) => {
  const { isMobile, isIOS, isAndroid } = useIsMobile()

  return <LandingContext.Provider value={{
    comingSoon,
    mode: isMobile
      ? (isIOS ? "ios" : "android")
      : "desktop"}}>
    <div className="neo__landing">
      <div className="neo__landing__inner">
        <div className="neo__landing__titleAndAppLogoHolder">
          <div className="neo__landing__appLogoHolder">
            <img src={appLogoSrc} />
          </div>
          <SectionTitle>{I18n.t(b("title"))}</SectionTitle>
        </div>
        <Carousel>
          {TEASER_IMAGE_PROJECTS.map((teaserProject) => {
            return <TeaserSlide key={teaserProject} project={teaserProject} comingSoon={!!comingSoon} />
          })}
        </Carousel>
        <LandingActionArea appLinks={init.applinks} />
        <LandingFeatureDescription />
      </div>
      <PrivacyInfo />
    </div>
  </LandingContext.Provider>
}

export default Landing
